"use client";

import * as Sentry from "@sentry/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { ClientError } from "graphql-request";
import { useRouter } from "next/navigation";
import type React from "react";
import { createContext, useContext, useEffect, useMemo } from "react";
import { analytics } from "@/lib/analytics";
import {
  clearSessionExpired,
  ensureSessionFresh,
  isSessionExpired,
  isTabVisible,
  subscribeSessionExpired,
} from "@/lib/auth/session-recovery";
import {
  ME_DOCUMENT,
  type MeBillingSummary,
  type MeChatSettings,
  type MeDocsSettings,
  type MeQueryData,
} from "@/lib/graphql/me-document";
import { useGraphqlQuery } from "@/lib/graphql/useGraphql";
import { uiMetricCount } from "@/lib/sentry/metrics";

/** Values read from URL query for auth redirects (e.g. nuqs `next` / `source` / `org`). */
export type AuthRedirectQuery = {
  next: string | null;
  source: string | null;
  org: string | null;
  /** @deprecated TODO(IL-391): remove after legacy ?account= URLs are gone. */
  account?: string | null;
};

const LOGIN_SUCCESS_SENTINEL = "sentry_login_success_emitted";

const isSafeRelativePath = (value: string): boolean => value.startsWith("/") && !value.startsWith("//");

export const getCurrentReturnToPath = (): string => {
  if (typeof window === "undefined") {
    return "/";
  }

  const currentPath = `${window.location.pathname}${window.location.search}`;
  return isSafeRelativePath(currentPath) ? currentPath : "/";
};

export const buildLoginUrl = (returnTo = getCurrentReturnToPath()): string => {
  const safeReturnTo = isSafeRelativePath(returnTo) ? returnTo : "/";
  // Omit next=/ — it's the default anyway and would shadow any radium_next cookie
  // (e.g. set by the chat OIDC logout flow to redirect back to /chat after re-auth).
  if (safeReturnTo === "/") {
    return "/login";
  }
  return `/login?next=${encodeURIComponent(safeReturnTo)}`;
};

const readCookie = (name: string): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const key = `${name}=`;
  const parts = document.cookie.split(";").map((item) => item.trim());
  const match = parts.find((item) => item.startsWith(key));
  if (!match) {
    return null;
  }

  const value = decodeURIComponent(match.slice(key.length));
  return value === "" ? null : value;
};

const clearCookie = (name: string) => {
  if (typeof document === "undefined") {
    return;
  }
  // biome-ignore lint/suspicious/noDocumentCookie: intentional cookie expiry — Cookie Store API not available in all target environments
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
};

export const resolveNext = (query: AuthRedirectQuery): string => {
  const returnToParam = query.next;
  if (returnToParam && isSafeRelativePath(returnToParam)) {
    return returnToParam;
  }

  const sourceParam = query.source;
  if (sourceParam === "chat") {
    return "/chat";
  }

  const cookieReturnTo = readCookie("radium_next");
  if (cookieReturnTo && isSafeRelativePath(cookieReturnTo)) {
    clearCookie("radium_next");
    return cookieReturnTo;
  }

  // Land on `/` first so the client can route using `me.onboarding_completed` without mounting
  // the dashboard shell (avoids a flash before redirect to `/onboarding`).
  return "/";
};

/** Attribution query keys forwarded to /api/auth/redirect for server-side capture. */
const ATTRIBUTION_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "gad_source",
  "gad_campaignid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/**
 * Builds a WorkOS AuthKit redirect URL with proper validation and query parameters.
 *
 * Attribution params (gclid, utm_*) present in the current page URL are forwarded
 * to /api/auth/redirect so the server can seal them into the radium_attribution
 * cookie before the WorkOS hop — this is the primary capture path.
 *
 * @param query - URL search values (from nuqs or tests)
 * @param screenHint - The screen hint to pass to WorkOS ("sign-in" or "sign-up")
 * @returns The redirect URL to `/api/auth/redirect` with all necessary query parameters
 */
export const buildWorkosRedirectUrl = (query: AuthRedirectQuery, screenHint: "sign-in" | "sign-up"): string => {
  const returnToPath = resolveNext(query);

  // TODO(IL-391): drop legacy `account` query fallback after cutover.
  const orgSlug = query.org ?? query.account ?? null;

  // Send a safe relative path so the backend can normalize to the active frontend
  // origin (e.g. https://localhost:8443 when developing through the gateway).
  // When running Next dev server directly on 3088/3000, route auth through the
  // gateway API to avoid local rewrite/cert mismatch issues.
  const isLocalDevUi =
    typeof window !== "undefined" && (window.location.port === "3088" || window.location.port === "3000");
  const authBase = isLocalDevUi ? "https://localhost:8443/api" : "/api";

  let redirectUrl = `${authBase}/auth/redirect?next=${encodeURIComponent(returnToPath)}&screen_hint=${screenHint}`;
  if (orgSlug) {
    redirectUrl += `&org=${encodeURIComponent(orgSlug)}`;
  }

  // Forward any attribution params from the current page URL to /api/auth/redirect
  // so the server captures them even when the client-rendered page is reached via
  // a direct link rather than a next.config redirect (which forwards them automatically).
  if (typeof window !== "undefined") {
    const currentParams = new URLSearchParams(window.location.search);
    for (const key of ATTRIBUTION_KEYS) {
      const value = currentParams.get(key);
      if (value) {
        redirectUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    }
  }

  return redirectUrl;
};

const ME_ERROR_SUMMARY_MAX_LEN = 300;

/** Pull a single line of detail from graphql-request / network errors (no stack). */
const extractMeQueryErrorRawMessage = (error: unknown): string => {
  if (error instanceof ClientError) {
    const first = error.response.errors?.[0]?.message;
    if (typeof first === "string" && first.trim().length > 0) {
      return first.trim();
    }
    const status = error.response.status;
    if (typeof status === "number" && status > 0) {
      return `Request failed with status ${status}`;
    }
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message.trim();
  }
  return "";
};

/**
 * Safe, short text for UI: no tokens, cookies, HTML, stack lines, or emails.
 * Returns null if nothing usable remains.
 */
const sanitizeMeErrorSummary = (raw: string): string | null => {
  if (!raw) {
    return null;
  }
  let s = raw.replace(/\r\n/g, "\n");
  const stackLike = s.search(/\n\s*at\s+/);
  if (stackLike !== -1) {
    s = s.slice(0, stackLike);
  }
  s = s.replace(/<[^>]{0,500}>/g, "");
  s = s.replace(/eyJ[a-zA-Z0-9_-]*\.eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_.-]*/g, "[token]");
  s = s.replace(/Bearer\s+\S+/gi, "Bearer [redacted]");
  s = s.replace(/\b(?:session|refresh|access|id)[_-]?token\b[=:]\s*\S+/gi, "token=[redacted]");
  s = s.replace(/\bcookie\s*:\s*\S+/gi, "cookie: [redacted]");
  s = s.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, "[email]");
  s = s.replace(/\s+/g, " ").trim();
  if (s.length > ME_ERROR_SUMMARY_MAX_LEN) {
    s = `${s.slice(0, ME_ERROR_SUMMARY_MAX_LEN - 1)}…`;
  }
  return s.length > 0 ? s : null;
};

interface User {
  id: string;
  org_id: string | null;
  email: string;
  name: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  jobtitle: string | null;
  avatarUrl?: string;
  role?: string;
}

export type CapacityStatus = MeQueryData["me"]["capacity_status"];
export type { MeBillingSummary, MeChatSettings, MeDocsSettings };

interface AuthContextType {
  user: User | null;
  capacityStatus: CapacityStatus | null;
  chatSettings: MeChatSettings | null;
  docsSettings: MeDocsSettings | null;
  billingSummary: MeBillingSummary | null;
  loading: boolean;
  /** TanStack `isPending` for the first `me` fetch only (not background refetch). */
  isMePending: boolean;
  /** From `me.onboarding_completed`; defined when `me` has loaded successfully. */
  onboardingCompleted: boolean | undefined;
  /** From `me.signup_conversion_pending` — armed until ackSignupConversion. Null while loading. */
  signupConversionPending: boolean | null;
  /** Server-computed platform-superuser flag derived from `me.extra_roles`. Null while loading. True only for internal Radium team members (WorkOS org role org-radium-superuser; exposed here as extra_roles 'radium-superuser') — never for customer org admins. */
  isAdmin: boolean | null;
  /** Active-org role slug from `me.org_role`. Null while loading or when the user has no seat. */
  orgRole: string | null;
  /** Active org kind from `me.org`; null while loading or when no org. */
  orgKind: string | null;
  /** Active org id from `me.org`; null while loading or when no org. */
  orgId: string | null;
  /** Active org display name from `me.org`. */
  orgName: string | null;
  /** Orgs the user can act in (memberships + home). */
  orgs: Array<{ id: string; kind: string; name: string }>;
  status: "loading" | "authenticated" | "unauthenticated" | "error";
  /** Present when `status === "error"`; sanitized, capped; no PII/tokens. */
  meErrorSummary: string | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  capacityStatus: null,
  chatSettings: null,
  docsSettings: null,
  billingSummary: null,
  loading: false,
  isMePending: true,
  onboardingCompleted: undefined,
  signupConversionPending: null,
  isAdmin: null,
  orgRole: null,
  orgKind: null,
  orgId: null,
  orgName: null,
  orgs: [],
  status: "loading",
  meErrorSummary: null,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const _router = useRouter();
  const queryClient = useQueryClient();

  const { data, error, isFetching, isPending, refetch } = useGraphqlQuery<MeQueryData>({
    queryKey: ["me"],
    document: ME_DOCUMENT,
    retry: (failureCount, err) => {
      // If cookies are being set/rotated, the first request can occasionally 401.
      // Retry briefly to avoid getting stuck in an unauthenticated UI state after login.
      if (!(err instanceof ClientError)) return false;
      if (err.response.status !== 401) return false;
      return failureCount < 2;
    },
    retryDelay: 250,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // Force a refetch on mount to ensure the network call appears in browser DevTools
  useEffect(() => {
    // Only refetch once on mount to ensure the request appears in network tab
    refetch().catch(() => {
      // Ignore errors here - they're already handled by the query
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Only refetch once on mount to ensure the request appears in network tab
    refetch,
  ]); // Empty deps: only run once on mount

  // Hard session death: drop stale `me` so status becomes unauthenticated and AuthGate can redirect.
  useEffect(() => {
    return subscribeSessionExpired(() => {
      void queryClient.cancelQueries({ queryKey: ["me"] });
      queryClient.removeQueries({ queryKey: ["me"] });
    });
  }, [queryClient]);

  const loading = isPending || isFetching;

  const me = data?.me;
  useEffect(() => {
    if (!me) {
      return;
    }

    // Counts authenticated sessions, not page loads: the `me` query resolves on
    // every mount, so without this sentinel the counter would track navigations.
    // signOut clears it so a logout/login cycle in the same tab counts again.
    if (typeof window !== "undefined" && !sessionStorage.getItem(LOGIN_SUCCESS_SENTINEL)) {
      sessionStorage.setItem(LOGIN_SUCCESS_SENTINEL, "1");
      uiMetricCount("ui.auth.login_success", 1, { outcome: "success" });
    }

    const featureFlags = Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>("FeatureFlags");
    featureFlags?.addFeatureFlag("system_flag.chat.enabled", me.chat_settings?.globally_enabled ?? false);
    featureFlags?.addFeatureFlag("system_flag.docs.enabled", me.docs_settings?.globally_enabled ?? false);
    featureFlags?.addFeatureFlag(
      "system_flag.capacity.gpu_available",
      me.capacity_status?.global_gpu_available ?? false,
    );
  }, [me]);
  const user: User | null = me
    ? {
        id: me.id,
        org_id: me.org_id,
        email: me.email,
        name: me.name ?? [me.first_name, me.last_name].filter(Boolean).join(" "),
        first_name: me.first_name,
        last_name: me.last_name,
        company: me.company,
        jobtitle: me.jobtitle,
      }
    : null;

  const capacityStatus: CapacityStatus | null = me?.capacity_status ?? null;
  const chatSettings: MeChatSettings | null = me?.chat_settings ?? null;
  const docsSettings: MeDocsSettings | null = me?.docs_settings ?? null;
  const billingSummary: MeBillingSummary | null = me?.billing_summary ?? null;
  const onboardingCompleted = me?.onboarding_completed;
  const signupConversionPending: boolean | null = me ? me.signup_conversion_pending : null;
  const isAdmin: boolean | null = me ? me.extra_roles.includes("radium-superuser") : null;
  const orgRole: string | null = me ? (me.org_role ?? null) : null;
  const orgKind: string | null = me?.org?.kind ?? null;
  const orgId: string | null = me?.org?.id ?? null;
  const orgName: string | null = me?.org?.name ?? null;
  const orgs = me?.orgs ?? [];

  const isUnauthorized = error instanceof ClientError && error.response.status === 401;

  const status: AuthContextType["status"] = loading
    ? "loading"
    : user
      ? "authenticated"
      : isUnauthorized
        ? "unauthenticated"
        : error
          ? "error"
          : "unauthenticated";

  // Successful `me` after re-login (full navigation) or recovery clears the expired latch.
  useEffect(() => {
    if (user) {
      clearSessionExpired();
    }
  }, [user]);

  // Proactive silent heal when the tab becomes active again (idle cookies may need rotation).
  useEffect(() => {
    if (!user) {
      return;
    }

    const healOnFocus = () => {
      if (!isTabVisible() || isSessionExpired()) {
        return;
      }
      void (async () => {
        const ok = await ensureSessionFresh();
        if (ok) {
          void queryClient.invalidateQueries({ queryKey: ["me"] });
        }
      })();
    };

    document.addEventListener("visibilitychange", healOnFocus);
    window.addEventListener("focus", healOnFocus);
    return () => {
      document.removeEventListener("visibilitychange", healOnFocus);
      window.removeEventListener("focus", healOnFocus);
    };
  }, [queryClient, user]);

  const meErrorSummary = useMemo(() => {
    if (status !== "error" || !error) {
      return null;
    }
    return sanitizeMeErrorSummary(extractMeQueryErrorRawMessage(error));
  }, [error, status]);

  const signOut = async () => {
    uiMetricCount("ui.auth.logout", 1);
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(LOGIN_SUCCESS_SENTINEL);
    }
    analytics.reset();

    // Cancel in-flight me fetches so removeQueries cannot refetch and re-identify.
    await queryClient.cancelQueries({ queryKey: ["me"] });
    queryClient.removeQueries({ queryKey: ["me"] });
    clearSessionExpired();

    // Navigate to logout endpoint which will:
    // 1. Clear the WorkOS session cookie
    // 2. Redirect to WorkOS logout URL (if session is valid)
    // 3. WorkOS will redirect back to /login
    // Using full-page navigation ensures browser follows redirect chain properly
    window.location.href = "/api/logout?next=/login";
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        capacityStatus,
        chatSettings,
        docsSettings,
        billingSummary,
        loading,
        isMePending: isPending,
        onboardingCompleted,
        signupConversionPending,
        isAdmin,
        orgRole,
        orgKind,
        orgId,
        orgName,
        orgs,
        status,
        meErrorSummary,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const useSession = () => {
  const { user, loading, isMePending } = useAuth();
  return {
    data: user ? { user } : null,
    // With a resolved user, ignore background `me` refetch (avoids save flashes).
    // Without a user, keep `loading` so 401 retries still gate login redirects.
    isPending: user ? isMePending : loading,
  };
};
